import React, { useState, useEffect, useContext } from 'react'

import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Header, Grid, Segment, Button, Table, Icon } from 'semantic-ui-react'

import Data, { IProject } from '../../../api/Data'
import { IEpisode } from '../../../api/Data'

import SubSection from '../../../components/common/SubSection'
import Title from '../../../components/common/Title'
import VideoEncodingCard from '../../../components/common/VideoEncodingCard'
import VideoErrorCard from '../../../components/common/VideoErrorCard'

import CompileTabEpisodeCard from './CompileTabEpisodeCard'
import CompileTabEpisodeModal from './CompileTabEpisodeModal'

import ExternalLink from '../../../components/common/ExternalLink'
import { GlobalContext } from '../../../contexts/GlobalContext'
import { downloadFileFromBrowser } from '../../../utils/sharedFunctions'

let autoFetchEpisodes: ReturnType<typeof setTimeout>

type BulkDownload = {
  presignedUrl: string
  fileName: string
  date: string
}

const CompileTab = ({ history, match, projectTitle, episodes, project, setEpisodes, exitProject }: IProps) => {
  // States
  const [episodeModal, setEpisodeModal] = useState<IEpisode | null>(null)

  const [bulkDownloads, setBulkDownloads] = useState<BulkDownload[]>([])
  const { dispatchGlobalContext } = useContext(GlobalContext)
  const projectData = project as IProject & { teamTermsUrl?: string }

  // Fetch episodes
  useEffect(() => {
    const fetchEpisodes = async () => {
      const { projectSlug } = match.params

      clearTimeout(autoFetchEpisodes)

      const { data, error } = await Data.getEpisodes(projectSlug)

      if (error) return alert('An error occurred')

      setEpisodes(data)

      const isEpisodesInProgress = data.some((episode: IEpisode) => episode.encodingStatus === 'encoding')

      // If stories are encoding, fetch stories every 5 seconds
      if (isEpisodesInProgress) autoFetchEpisodes = setTimeout(fetchEpisodes, 5000)
    }

    const fetchBulkDownloadClips = async () => {
      const projectSlug = match.params.projectSlug
      const { data, error } = await Data.getBulkDownloads(projectSlug)

      if (error) return alert('An error occurred')

      const sortedBulkDownloads = data.sort((a: BulkDownload, b: BulkDownload) => {
        return new Date(b.date).getTime() - new Date(a.date).getTime()
      })

      try {
        setBulkDownloads(sortedBulkDownloads as BulkDownload[])
      } catch (error) {
        console.log(error)
      }
    }

    fetchEpisodes()
    fetchBulkDownloadClips()

    return () => clearTimeout(autoFetchEpisodes)
  }, [match.params, setEpisodes])

  const deleteEpisode = async (episodeId: number) => {
    const { error } = await Data.deleteEpisode(episodeId)

    if (error) {
      return alert(`An error occurred => ${error}`)
    }

    window.location.reload()
  }

  const convertDateAndTime = (date: string) => {
    const dateObj = new Date(date)
    const dateStr = dateObj.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    const timeStr = dateObj.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    })
    return `${dateStr} - ${timeStr}`
  }
  const downloadCsvReport = async () => {
    const { data, error } = await Data.getCsvReport(project.slug)

    if (error) {
      if (error.response.status === 403) {
        return dispatchGlobalContext({ type: 'show update plan modal' })
      }
      return alert('An error occurred')
    }

    const { reportData, reportFileName } = data

    return downloadFileFromBrowser({ data: reportData, type: 'text/csv', fileName: reportFileName })
  }

  return (
    <>
      <Title title={projectTitle}>
        <>
          <Button compact onClick={exitProject}>
            Close
          </Button>
        </>
      </Title>

      <SubSection hasGreyBackground>
        <>
          <Header as="h1" style={{ marginBottom: '2rem', fontWeight: 'bold' }}>
            Project Downloads
            <Header.Subheader>
              Access all your video compilations, consent logs and bulk export folders in one place. Video stories
              created will appear here, as will zipped bulk export folders. The consent log is a dynamically created CSV
              file that updates every time a new video submission is received.
            </Header.Subheader>
          </Header>

          <Segment>
            <Header as="h2" style={{ fontWeight: 'bold' }}>
              Project Report (Consent Log)
            </Header>

            <Header as="h4">
              Vloggi automatically generates a consent evidence report for every project, capturing contributor details
              and upload records including consent reference, contributor name, email address, IP address, consent
              timestamp, upload timestamp, file status, content owner, privacy policy link and any project-level
              declarations presented during upload. Where a consent reference is present, the report records that the
              contributor completed Vloggi’s legally reviewed Content Assignment Form, supporting rights clearance,
              copyright assignment, consent tracking and compliance record keeping.{' '}
              <ExternalLink url="https://vloggi.com/content-assignment-agreement/">
                View the Content Assignment Form
              </ExternalLink>
              . Click here to download the CSV consent report for <strong>"{projectTitle}"</strong>.
            </Header>

            <Button secondary onClick={downloadCsvReport} style={{ whiteSpace: 'nowrap' }}>
              <Icon name="file excel"></Icon>
              Project Report
            </Button>
          </Segment>

          <Segment>
            <Header as="h2" style={{ fontWeight: 'bold' }}>
              Bulk Exports
            </Header>

            <Header as="h4">
              Bulk downloads can take between 1 to 15 minutes to complete. Try refreshing this page if you don't see
              your download.&nbsp;
              <ExternalLink url="https://vloggi.com/docs/how-to-download-video-submissions-in-bulk-as-zipped-folder-on-vloggi/">
                Read more.
              </ExternalLink>
            </Header>

            {bulkDownloads?.length > 0 ? (
              <Table celled>
                <Table.Body>
                  {bulkDownloads.map((download) => (
                    <Table.Row key={download.fileName}>
                      {/* Format the date to be more readable */}
                      <Table.Cell>
                        <Header as="h4">Bulk download created on {convertDateAndTime(download.date)}</Header>
                      </Table.Cell>
                      <Table.Cell textAlign="center" style={{ width: 0, whiteSpace: 'nowrap' }}>
                        <a href={download.presignedUrl} download={download.fileName}>
                          <Button primary style={{ margin: 0 }}>
                            <Icon name="file archive"></Icon>
                            Download Archive
                          </Button>
                        </a>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            ) : (
              <Header as="h5">You don't have any bulk downloads.</Header>
            )}
          </Segment>

          <Segment>
            <Header as="h2" style={{ fontWeight: 'bold' }}>
              Compilations
            </Header>

            {episodes?.length > 0 ? (
              <Grid stackable columns={4}>
                {episodes.map((episode) => (
                  <Grid.Column key={episode.id}>
                    {episode.encodingStatus === 'encoded' && (
                      <CompileTabEpisodeCard episode={episode} setEpisodeModal={setEpisodeModal} />
                    )}
                    {episode.encodingStatus === 'error' && (
                      <VideoErrorCard videoId={episode.id} handleDelete={deleteEpisode} />
                    )}
                    {episode.encodingStatus === 'encoding' && <VideoEncodingCard status={episode.status} />}
                  </Grid.Column>
                ))}
              </Grid>
            ) : (
              <Header as="h5">
                You have not made a story yet. To merge video submission clips together without video editing software
                you can use the video story (compilation) feature in Vloggi. To create a compilation, click the top
                right corner icon of the clips you want to include from the Curate tab.&nbsp;
                <ExternalLink url="https://vloggi.com/docs/how-do-i-make-video-compilations-without-editing-software/">
                  Read more.
                </ExternalLink>
              </Header>
            )}

            {episodeModal && (
              <CompileTabEpisodeModal
                episode={episodeModal}
                setEpisodeModal={setEpisodeModal}
                deleteEpisode={deleteEpisode}
              />
            )}
          </Segment>
        </>
      </SubSection>
    </>
  )
}

interface IProps extends RouteComponentProps<any> {
  projectTitle: string
  project: IProject
  episodes: IEpisode[]
  setEpisodes: (arg: IEpisode[]) => void
  exitProject: () => void
}

export default withRouter(CompileTab)
